import spartaStoreUtility from "utilities/global/sparta-store-utility/3.0.4/js/sparta-store-utility";
import Handlebars from "utilities/vendor/handlebars/4.7.6/js/handlebars";
import hbs from "utilities/vendor/handlebars/4.7.6/js/handlebars";
/* eslint-disable require-jsdoc */

class SpartaRequireUtility {
  constructor() {
    this.resourceLoadListeners = new Map();
    this._dynamicModulePromiseCache = new Map();
    this.deps = {};
    this.flattenedDeps = [];
  }

  get dynamicModulePromiseCache() {
    return this._dynamicModulePromiseCache;
  }

  set dynamicModulePromiseCache(moduleCache) {
    this._dynamicModulePromiseCache = moduleCache;
  }

  // an accessor property to return the dependenciesMap;
  get dependencyList() {
    return this.deps;
  }

  /**
   * To register listener callback for onResourceLoad event of requirejs.
   * @param {string} resourceUrl Resource(Module/Helper/utilities) url.
   * @param {Function} callback  callback to be invoked during resource load time.
   */
  registerResourceLoadListener(resourceUrl, callback) {
    this.resourceLoadListeners.set(resourceUrl, callback);
  }

  /**
   * To register handlebars helpers
   * @returns {Promise} Promise that resolves once handlebars are registered
   */
  registerHandlerbarHelpers() {
    return new Promise((resolve, reject) => {
      if (this.helpersRegistered) {
        resolve();
      } else {
        this.getResourcesByWords('utilities/helpers')
          .then((utilities) => {
            utilities.forEach((util) => {
              util.register(Handlebars);
            });
            this.helpersRegistered = true;
            resolve();
          })
          .catch(reject);
      }
    });
  }

  /**
   * To reset the helpersRegistered flag
  * @returns {void}
   */
  resetHandlebarHelperFlag() {
    this.helpersRegistered = false;
  }

  /**
   * To un register listener callback for onResourceLoad event of requirejs.
   * @param {string} resourceUrl Resource(Module/Helper/utilities) url.
   */
  unRegisterResourceLoadListener(resourceUrl) {
    this.resourceLoadListeners.delete(resourceUrl);
  }

  /**
   * Promisifies require.js for ease of use
   * @param {Array} urls Array of URLs to call require.js with
   * @returns {Promise} Promise that wraps require.js
  */
  getRequirePromise(urls) {
    return new Promise((resolve, reject) => {
      this.getRequire()(urls, resolve, reject);
    });
  }

  /**
   * To get resource with its dependencies.
   * @param {string} url Resource url
   * @return {Promise<object>} Return loaded resource with depdencies.
   * @example
   * { context, map, depArray }
   * To get resource for given url use `context.defined[map.id]`
   * To get dependencies for given resource use depArray.
   *
   * Note: Usually we need not to load dependencies explicitly requirejs can handle it, this is added to handle special case where
   * we need to register configured helpers with on-demand modules.
   */
  getResourceWithDependencies(url) {
    let dynamicModulePromise = this._dynamicModulePromiseCache.get(url);
    if (dynamicModulePromise) {
      return dynamicModulePromise;
    }

    this.setOnResourceLoad(this.resourceLoadListeners);  // this used to be in the constructor

    dynamicModulePromise = new Promise(async (resolve, reject) => {
      try {
        this.registerResourceLoadListener(url, (context, map, depArray) => {
          if (url === map.id) {
            resolve({ context, map, depArray });
          }
        });
        const requireJs = this.getRequire();
        try {
          // To make sure module is loaded always
          // Added try catch to prevent break in private api with future upgrades
          requireJs.s.contexts._.defined[url] && requireJs.undef(url);
        } catch (error) {
          console.log(`Error in requirejs.undef(${url})`, error);
        }
        await this.getRequirePromise([url]);
      } catch (error) {
        reject(error);
      } finally {
        this.unRegisterResourceLoadListener(url);
      }
    });
    this._dynamicModulePromiseCache.set(url, dynamicModulePromise);
    return dynamicModulePromise;
  }

  // Get require, based on if we're inside a widget or not
  getRequire() {
    const widgetContext = spartaStoreUtility.getData('applicationContext');
    let requireJs;

    if (widgetContext) {
      const { name, version } = widgetContext;
      const widgetVersion = window.sparta.require[name][version];
      requireJs = (widgetContext.id) ? widgetVersion[widgetContext.id].require : widgetVersion.require;
    } else if (window.require) {
      requireJs = window.require;
    } else if (window.sparta && window.sparta.require) {
      requireJs = window.sparta.require;
      console.warn('fallback to window.sparta.require since window.require is undefined');
    } else {
      console.warn('requirejs is undefined');
    }

    this.deps = requireJs.dependenciesMap || {};

    return requireJs;
  }

  /**
   * To set onResourceLoad callback on requirejs.
   * @param {Array<string>} Registered resourceLoadListeners.
   */
  setOnResourceLoad(resourceLoadListeners) {
    const requireJs = this.getRequire();
    if (requireJs && !requireJs.onResourceLoad) {
      requireJs.onResourceLoad = function (context, map, depArray) {
        if (!requireJs.dependenciesMap) {
          requireJs.dependenciesMap = {};
        }
        depArray.forEach((dep) => {
          if (dep.id === 'exports') {
            return;
          }
          requireJs.dependenciesMap[map.id] = requireJs.dependenciesMap[map.id] || [];
          requireJs.dependenciesMap[map.id].push(dep.id);
        });
        const callback = map && map.id && resourceLoadListeners.get(map.id);
        callback && callback(context, map, depArray);
      };
    }
  }

  /**
   * Get a flattened dependency list of unique dependencies
   * @param {Null}
   * @return {Array} Return an array of dependency paths
   **/
  flattenDependencies() {
    const requireJS = this.getRequire();
    const dependencies = this.dependencyList;
    return Object.keys(dependencies).reduce((r, k) => {
      return [...new Set([...r, ...dependencies[k]])];
    }, []).sort();
  }

  /**
   * Get a single resource via it's name
   * @param {Strings} resourceName the name of the resource
   * @return {Promise<object>} Return a promise of the particular resource
   * @example getResourcesByName('sparta-style-utility'); // return a resource
   **/
  getResourceByName(resourceName) {
    const path = this.filterResourcesByWords(resourceName)[0];
    return this.getRequirePromise([path]).then((resource) => new Promise((resolve, reject) => {
      if (!resource) return reject(new Error(`${module} is not in the page dependencies.`));
      return resolve(resource);
    })).catch((error) => error);
  }

  /**
   * Get a list of resource paths via search term words
   * @param {Strings} words an argument list of search terms
   * @return {Promise<object>} Return a promise of a list of resources
   * @example getResourcesByWords('/stores/', 'index'); // return all store paths that contain an index file
   **/
  filterResourcesByWords(...words) {
    let deps = this.flattenedDeps;
    deps = (deps.length) ? deps : this.flattenDependencies();
    const ids = Object.keys(deps).filter((name) => words.every((word) => deps[name].includes(word)));
    return ids.map((id) => deps[id]);
  }

  /**
   * Get resources via search term words
   * @param {Strings} words an argument list of search terms
   * @return {Promise<object>} Return a promise of a list of resources
   * @example getResourcesByWords('/stores/'); // return all store resources that match the search words
   **/
  getResourcesByWords(...words) {
    const paths = this.filterResourcesByWords(...words);
    return this.getResourcesByPaths(...paths);
  }

  /**
   * Get a list of resources from a list of paths.
   * @param {Strings} paths an argument list of path ids
   * @return {Promise<object>} Return a promise of a list of resources
   * @example getResourcesByPaths('sparta-vue-module-utility');
   **/
  getResourcesByPaths(...paths) {
    const promises = paths.map((path) => this.getRequirePromise([path]));
    return promises.reduce((promiseChain, currentTask) => { // sequential resolution
      return promiseChain.then((chainResults) =>
        currentTask.then((currentResult) => [...chainResults, currentResult]));
    }, Promise.resolve([]));
  }

  /**
   * Get a list of dependency paths for a given module.
   * @param {string} module id of specific module
   * @return {Array} Return a list of paths of dependencies
   * @example moduleDependencyList('sparta-vue-module-utility');
   **/
  moduleDependencyList(module) {
    const depEntries = Object.entries(this.deps); // FIXME: `this.deps` could be undefined
    const modEntry = depEntries.filter((el) => el[0].endsWith(module));
    const modDeps = modEntry && modEntry[0][1];
    return modDeps;
  }
}

export default new SpartaRequireUtility();
